// Record: the operator's voice and a screen he picks, filed as a call, with no
// camera and no Breeze room. Adam, 2026-10-29: "record voice and screen
// record, no video and no having to go in the breeze room". The browser does
// the capture; the pieces go up the way Listen's do (lib/listenRecorder.ts)
// and the Worker joins them, transcribes the sound and sends the call to the
// history when Stop is pressed.

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import type { AccountUser, ListenSaved } from "@breeze/core";
import { MicIcon, ScreenShareIcon } from "../components/icons";
import { api } from "../lib/api";
import { ListenRecorder } from "../lib/listenRecorder";
import { openMicrophone, stopTrack } from "../lib/listenMedia";
import { navigate } from "../lib/nav";
import { ownRoomPath } from "../lib/storage";

type Phase = "idle" | "asking" | "recording" | "saving" | "done" | "failed";

interface DisplayOptions extends DisplayMediaStreamOptions {
  readonly systemAudio?: "include" | "exclude";
  readonly selfBrowserSurface?: "include" | "exclude";
}

const DONE_HOLD_MS = 2200;

function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const two = (n: number): string => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}

function newCode(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return `r${[...bytes].map((b) => b.toString(36).padStart(2, "0")).join("")}`;
}

/** The picked screen, with its sound when the browser offers it. The sound is
 * a bonus here: a screen shared without it is still recorded. */
async function pickScreen(): Promise<{ video: MediaStreamTrack; audio: MediaStreamTrack | null } | "cancelled" | "unsupported"> {
  const media = navigator.mediaDevices;
  if (!media?.getDisplayMedia) return "unsupported";
  try {
    const stream = await media.getDisplayMedia({
      audio: true,
      systemAudio: "include",
      selfBrowserSurface: "exclude",
      video: { frameRate: { ideal: 5, max: 5 }, width: { max: 1280 }, height: { max: 720 } },
    } as DisplayOptions);
    const video = stream.getVideoTracks()[0];
    if (!video) {
      for (const t of stream.getTracks()) stopTrack(t);
      return "cancelled";
    }
    return { video, audio: stream.getAudioTracks()[0] ?? null };
  } catch (err) {
    const name = (err as Error)?.name;
    return name === "NotAllowedError" || name === "AbortError" ? "cancelled" : "unsupported";
  }
}

/** In the Seamless Calls tab's frame the page tells the card it is finished, so
 * the card takes the frame away and offers Record again. */
function tellParent(message: { state: string; height?: number; code?: string; callId?: string; startedMs?: number; endedMs?: number }): void {
  try {
    if (window.parent !== window) window.parent.postMessage({ breeze: "record", ...message }, "*");
  } catch {
    // Not framed, or the parent is gone.
  }
}

export function RecordScreen({ me, embed = false }: { readonly me: AccountUser; readonly embed?: boolean }): JSX.Element {
  const [phase, setPhase] = useState<Phase>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [trouble, setTrouble] = useState(false);
  const [saved, setSaved] = useState<ListenSaved | null>(null);
  const recorder = useRef<ListenRecorder | null>(null);
  const code = useRef("");
  const startedAt = useRef(0);
  const endedAt = useRef(0);
  const callId = useRef("");
  const unfiled = useRef<ListenSaved | null>(null);
  const tracks = useRef<MediaStreamTrack[]>([]);
  const stopping = useRef(false);

  const release = useCallback((): void => {
    for (const t of tracks.current) stopTrack(t);
    tracks.current = [];
  }, []);

  // The pieces are already on the server by now; filing only asks the Worker
  // to join them, so it can be retried without recording again.
  const file = useCallback(
    async (result: ListenSaved): Promise<void> => {
      setPhase("saving");
      try {
        const filed = await api<{ callId?: string }>("/api/listen/record", {
          method: "POST",
          body: { code: code.current, startedMs: startedAt.current, endedMs: endedAt.current, name: me.displayName || "Adam" },
        });
        callId.current = filed?.callId ?? "";
        setSaved(result);
        setPhase("done");
      } catch {
        unfiled.current = result;
        setPhase("failed");
        setMessage("The recording is saved, but filing the call did not work.");
        // The card already lifted at Stop; the parent brings it back.
        if (embed) tellParent({ state: "failed", code: code.current });
      }
    },
    [embed, me.displayName],
  );

  const stop = useCallback(async (): Promise<void> => {
    const rec = recorder.current;
    if (!rec || stopping.current) return;
    stopping.current = true;
    setPhase("saving");
    endedAt.current = Date.now();
    // The card does not wait for the filing: it lifts into History the moment Stop is pressed.
    if (embed) tellParent({ state: "stopping", code: code.current, startedMs: startedAt.current, endedMs: endedAt.current });
    const result = await rec.flush();
    release();
    recorder.current = null;
    stopping.current = false;
    if (result === "none") {
      setSaved("none");
      setPhase("failed");
      setMessage("Nothing was recorded.");
      if (embed) tellParent({ state: "failed", code: code.current });
      return;
    }
    await file(result);
  }, [embed, file, release]);

  const retry = async (): Promise<void> => {
    const result = unfiled.current;
    unfiled.current = null;
    setMessage(null);
    if (result) await file(result);
  };

  const start = async (): Promise<void> => {
    setMessage(null);
    setSaved(null);
    unfiled.current = null;
    setPhase("asking");
    // The picker must open straight from the click, so the screen comes first.
    const screen = await pickScreen();
    if (screen === "cancelled") {
      setPhase("idle");
      return;
    }
    if (screen === "unsupported") {
      setPhase("failed");
      setMessage("This browser cannot record a screen. Use Chrome on a computer.");
      return;
    }
    const mic = await openMicrophone();
    if (!mic) {
      stopTrack(screen.video);
      stopTrack(screen.audio);
      setPhase("failed");
      setMessage("The microphone is not available. Allow it for this site and try again.");
      return;
    }
    code.current = newCode();
    stopping.current = false;
    const rec = new ListenRecorder(code.current);
    rec.addAudio("mic", mic);
    if (screen.audio) rec.addAudio("screen", screen.audio);
    rec.startWith(screen.video);
    if (!rec.hasStarted()) {
      stopTrack(mic);
      stopTrack(screen.audio);
      stopTrack(screen.video);
      setPhase("failed");
      setMessage("This browser could not start recording.");
      return;
    }
    recorder.current = rec;
    tracks.current = [mic, ...(screen.audio ? [screen.audio] : [])];
    startedAt.current = Date.now();
    setElapsed(0);
    setTrouble(false);
    setPhase("recording");
    // Stopping the share from the browser's own bar ends the recording too.
    screen.video.addEventListener("ended", () => void stop(), { once: true });
  };

  useEffect(() => {
    if (phase !== "recording") return;
    const timer = setInterval(() => {
      setElapsed(Date.now() - startedAt.current);
      setTrouble(recorder.current?.state() === "trouble");
    }, 500);
    const warn = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      clearInterval(timer);
      window.removeEventListener("beforeunload", warn);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "done") return;
    const timer = setTimeout(() => (embed ? tellParent({ state: "ended", code: code.current, ...(callId.current ? { callId: callId.current } : {}) }) : navigate(ownRoomPath())), DONE_HOLD_MS);
    return () => clearTimeout(timer);
  }, [phase, embed]);

  useEffect(() => release, [release]);

  useEffect(() => {
    if (!embed) return;
    document.documentElement.setAttribute("data-embed", "1");
    return () => document.documentElement.removeAttribute("data-embed");
  }, [embed]);

  // In the frame: how tall the row is, so the card around it can match.
  const whole = useRef<HTMLDivElement | null>(null);
  const phaseRef = useRef<Phase>("idle");
  phaseRef.current = phase;
  useEffect(() => {
    const el = whole.current;
    if (!embed || !el || typeof ResizeObserver === "undefined") return;
    let sent = 0;
    const report = (): void => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (!height || height === sent) return;
      sent = height;
      tellParent({ state: phaseRef.current, height });
    };
    const watch = new ResizeObserver(report);
    watch.observe(el);
    report();
    return () => watch.disconnect();
  }, [embed]);

  if (embed) {
    const busy = phase === "saving" || phase === "done";
    const word = busy
      ? phase === "saving"
        ? "Saving"
        : saved === "partial"
          ? "Saved, with a gap"
          : "Saved"
      : phase === "recording"
        ? trouble
          ? "Pieces are not reaching the server"
          : `Recording ${clock(elapsed)}`
        : (message ?? "Your voice and a screen you pick");
    return (
      <div className="linl--embed" data-testid={busy ? "record-saved" : undefined} data-saved={busy ? (saved ?? "saving") : undefined}>
        <div className="linl-wrap" ref={whole}>
          <div className="linl" data-state={phase}>
            <span className="lsi__dot" aria-hidden="true" data-quiet={phase === "recording" ? "0" : "1"} />
            <span className="linl__word" role={phase === "failed" ? "alert" : "status"}>
              {word}
            </span>
            {phase === "recording" ? (
              <button type="button" className="lnotes__stop" onClick={() => void stop()} data-testid="record-stop">
                Stop
              </button>
            ) : busy ? null : (
              <button type="button" className="lnotes__btn" onClick={() => void (unfiled.current ? retry() : start())} disabled={phase === "asking"} data-testid="record-start">
                {unfiled.current ? "Try filing again" : "Start"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "saving" || phase === "done") {
    return (
      <div className="listen listen--center" data-testid="record-saved" data-saved={saved ?? "saving"}>
        <p className="listen__problem" role="status">
          {phase === "saving" ? "Saving" : saved === "partial" ? "Saved, with a gap" : "Saved. It will be in your calls shortly."}
        </p>
      </div>
    );
  }

  const recording = phase === "recording";
  return (
    <div className="listen">
      <div className="listen__main">
        <header className="listen__head">
          <div className="listen__title">
            <h1>Record</h1>
          </div>
          <div className="listen__head-actions">
            {recording ? (
              <button type="button" className="listen__btn listen__btn--end" onClick={() => void stop()} data-testid="record-stop">
                Stop
              </button>
            ) : (
              <button type="button" className="listen__btn" onClick={() => navigate(ownRoomPath())}>
                Back
              </button>
            )}
          </div>
        </header>

        <p className="listen__lead" role="status">
          {recording ? (trouble ? "Pieces are not reaching the server. Stay on this page." : `Recording ${clock(elapsed)}`) : "Your voice and the screen you pick. No camera, no room."}
        </p>

        <section className="listen__sides" aria-label="What is recorded">
          <article className="listen__side" data-status={recording ? "on" : "idle"}>
            <div className="listen__side-top">
              <MicIcon size={20} />
              <div className="listen__side-name">
                <strong>Your microphone</strong>
                <span>Voice</span>
              </div>
            </div>
          </article>
          <article className="listen__side" data-status={recording ? "on" : "idle"}>
            <div className="listen__side-top">
              <ScreenShareIcon size={20} />
              <div className="listen__side-name">
                <strong>Your screen</strong>
                <span>The window or screen you pick, with its sound when offered</span>
              </div>
            </div>
          </article>
        </section>

        {message ? (
          <p className="listen__side-note" role="alert">
            {message}
          </p>
        ) : null}

        {recording ? null : (
          <button
            type="button"
            className="listen__btn listen__btn--primary"
            onClick={() => void (unfiled.current ? retry() : start())}
            disabled={phase === "asking"}
            data-testid="record-start"
          >
            {unfiled.current ? "Try filing again" : "Start recording"}
          </button>
        )}

        <ul className="listen__notes">
          <li>Tell anyone who can be heard that you are recording.</li>
          <li>To hear a call or video in the recording, pick the tab or the whole screen and turn on Share audio.</li>
          <li>Stop files it with the rest of your calls, with a transcript and a summary.</li>
        </ul>
      </div>
    </div>
  );
}
